import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BASIS_LABELS,
  PURPOSES,
  PURPOSE_BASES,
  PURPOSE_LABELS,
  ROUTE_LABELS,
  defaultBasisFor,
  isFiscal,
  requiredReleaseRole,
  type BasisOfValue,
  type Purpose,
} from '@ave/shared';
import { api } from '../lib/api';
import { Icon } from './icons';
import { Badge } from './ui';
import { useSession } from '../App';

/**
 * Stage 0, as a dialog.
 *
 * Purpose is asked first and everything else follows from it, because that
 * is how the engine works: the purpose locks the basis, decides whether the
 * valuation must resolve to a value per holding, and selects the corridors
 * and disclosures available downstream. Showing those consequences as the
 * user picks is the cheapest possible moment to catch a wrong purpose —
 * the alternative is discovering it at the review gate.
 */
export function NewEngagement({ onClose }: { onClose: () => void }) {
  const me = useSession();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState<Purpose>('iht_estate');
  const [basis, setBasis] = useState<BasisOfValue>(defaultBasisFor('iht_estate'));
  const [valuationDate, setValuationDate] = useState('');
  const [clientRef, setClientRef] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allowedBases = useMemo(() => PURPOSE_BASES[purpose], [purpose]);
  const basisLocked = allowedBases.length === 1;

  useEffect(() => {
    // The purpose changing re-derives the basis: an IHT valuation on
    // anything but the s.160 basis is not an IHT valuation, and the API
    // and a database constraint both refuse it.
    if (!allowedBases.includes(basis)) setBasis(defaultBasisFor(purpose));
  }, [purpose, allowedBases, basis]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const { engagement } = await api.createEngagement({
        name,
        purpose,
        basisOfValue: basis,
        valuationDate,
        clientRef: clientRef || undefined,
        notes: notes || undefined,
      });
      navigate(`/engagements/${engagement.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the engagement');
      setBusy(false);
    }
  };

  const valid = name.trim().length > 1 && /^\d{4}-\d{2}-\d{2}$/.test(valuationDate);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-10 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card animate-fade-up w-full max-w-2xl overflow-hidden">
        <div className="h-1 w-full bg-gradient-to-r from-teal-500 to-teal-600" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">New engagement</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Stage 0 — purpose, basis and valuation date. Everything downstream is gated on these
              three answers.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <Icon name="x" className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <label className="block">
            <span className="label">Engagement name</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Marchpole Estates Limited — estate of Mrs E. Warrender"
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </label>

          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className="label">Purpose</span>
              <select
                value={purpose}
                onChange={(e) => setPurpose(e.target.value as Purpose)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                {PURPOSES.map((p) => (
                  <option key={p} value={p}>
                    {PURPOSE_LABELS[p]}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="label">
                Basis of value
                {basisLocked ? (
                  <span className="ml-1.5 font-normal normal-case tracking-normal text-slate-400">
                    locked by the purpose
                  </span>
                ) : null}
              </span>
              <select
                value={basis}
                disabled={basisLocked}
                onChange={(e) => setBasis(e.target.value as BasisOfValue)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:bg-slate-50 disabled:text-slate-500"
              >
                {allowedBases.map((b) => (
                  <option key={b} value={b}>
                    {BASIS_LABELS[b]}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="label">Valuation date</span>
              <input
                type="date"
                value={valuationDate}
                onChange={(e) => setValuationDate(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
              <span className="mt-1 block text-xs text-slate-400">
                Pins the standards corpus and the corridors that apply.
              </span>
            </label>

            <label className="block">
              <span className="label">Client reference</span>
              <input
                value={clientRef}
                onChange={(e) => setClientRef(e.target.value)}
                placeholder="Optional"
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </label>
          </div>

          <label className="block">
            <span className="label">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Background a reviewer would want — the shape of the holding, anything unusual"
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </label>

          {/* What the chosen purpose commits the engagement to. */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
            <p className="label">What this purpose sets</p>
            <ul className="mt-2 space-y-1.5">
              <Consequence>
                Basis of value is {basisLocked ? 'fixed at' : 'restricted to'}{' '}
                <strong className="font-medium text-slate-800">{BASIS_LABELS[basis]}</strong>
              </Consequence>
              {isFiscal(purpose) ? (
                <>
                  <Consequence>
                    Must resolve to a value <strong className="font-medium text-slate-800">per
                    holding</strong> at the top entity; unit values are build-up only
                  </Consequence>
                  <Consequence>
                    Related property (IHTA s.161) must be addressed on every holding before Stage 0
                    closes
                  </Consequence>
                  <Consequence>
                    Any external property valuation must permit disclosure to HMRC, or release is
                    blocked
                  </Consequence>
                </>
              ) : (
                <Consequence>
                  Commercial purposes use the wider discount and latent-tax corridors
                </Consequence>
              )}
              <Consequence>
                Release requires approval from{' '}
                <strong className="font-medium text-slate-800">
                  {requiredReleaseRole(me.tenant.route) === 'acumon_reviewer'
                    ? 'an Acumon reviewer'
                    : 'a reviewer'}
                </strong>{' '}
                <Badge tone="slate">{ROUTE_LABELS[me.tenant.route].split('—')[0]?.trim()}</Badge>
              </Consequence>
            </ul>
          </div>

          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-3.5">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
            {busy ? 'Creating…' : 'Create engagement'}
            <Icon name="arrowRight" className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Consequence({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-sm text-slate-600">
      <Icon name="check" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-600" strokeWidth={2.5} />
      <span>{children}</span>
    </li>
  );
}
